import { useParams, Link, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight, Sparkles, CheckCircle2, Wrench } from 'lucide-react'
import { getBlogPost, BLOG_POSTS } from '../data/blogPosts'
import bpEn from '../data/blogPosts.en.json'
import bpDe from '../data/blogPosts.de.json'
import bpPl from '../data/blogPosts.pl.json'
import bpSk from '../data/blogPosts.sk.json'
import bpIt from '../data/blogPosts.it.json'
import bpHu from '../data/blogPosts.hu.json'
import bpUk from '../data/blogPosts.uk.json'
import bpEs from '../data/blogPosts.es.json'
import bpFr from '../data/blogPosts.fr.json'
import bpNl from '../data/blogPosts.nl.json'
import bpJa from '../data/blogPosts.ja.json'
import { useSeoHead, SITE } from '../lib/seo'
import { resolveBlogSlug, blogPath, relocalizePath } from '../lib/localizedUrl'

const TRANS: Record<string, any[]> = { en: bpEn, de: bpDe, pl: bpPl, sk: bpSk, it: bpIt, hu: bpHu, uk: bpUk, es: bpEs, fr: bpFr, nl: bpNl, ja: bpJa }
import ShareBar from '../components/ShareBar'
import NotFoundPage from './NotFoundPage'

export default function BlogPostPage() {
  const { slug } = useParams()
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const lang = i18n.language || 'cs'
  // URL slug může být lokalizovaný i starý český → kanonický český slug.
  const czSlug = slug ? resolveBlogSlug(slug, lang) : null
  const base = czSlug ? getBlogPost(czSlug) : undefined
  // POZOR: useSeoHead je hook → musí se volat VŽDY (před early-return redirectem),
  // jinak React spadne („Rendered fewer hooks"). Proto post počítáme jako nullable.
  const tr = base ? (TRANS[lang] || (lang !== 'cs' && TRANS.en) || []).find((x) => x.slug === base.slug) : undefined
  const post = base ? ((tr ? { ...base, ...tr } : base) as typeof base) : null
  const canonicalPath = post ? blogPath(post.slug, lang) : undefined
  useSeoHead({
    title: post?.metaTitle,
    description: post?.metaDesc,
    canonicalPath,
    hreflangPath: post ? (l) => blogPath(post.slug, l) : undefined,
    image: post ? `${SITE}/og/blog-${post.slug}.png` : undefined,
    jsonLd: post ? [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: post.title, description: post.metaDesc,
        datePublished: post.date, dateModified: post.date,
        author: { '@type': 'Organization', name: post.author },
        publisher: { '@type': 'Organization', name: 'SEO Tools', url: SITE },
        mainEntityOfPage: SITE + canonicalPath,
      },
      ...(post.faq ? [{
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: post.faq.map((f) => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }] : []),
    ] : undefined,
  })

  // Early-returny AŽ po hooku.
  if (!base || !post) return <NotFoundPage />
  const want = blogPath(post.slug, lang)
  if (location.pathname !== want) return <Navigate to={want} replace />

  return (
    <article className="max-w-3xl mx-auto px-4 py-10">
      <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-6">
        <Link to="/blog" className="inline-flex items-center gap-1.5 hover:text-primary-500 transition">
          <ArrowLeft className="w-4 h-4" />{t('blog.title', { defaultValue: 'Blog' })}
        </Link>
      </nav>

      <div className="flex flex-wrap gap-2 mb-3">
        {post.tags.map((tag) => (
          <span key={tag} className="px-2.5 py-0.5 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 text-xs font-semibold">{tag}</span>
        ))}
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">{post.title}</h1>
      <div className="flex items-center gap-4 text-sm text-gray-400 mt-4 mb-8">
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(post.date).toLocaleDateString('cs-CZ')}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" />{post.readMins} min čtení</span>
        <span>{post.author}</span>
      </div>

      <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-8">{post.excerpt}</p>

      <div className="space-y-8">
        {post.sections.map((s, i) => (
          <section key={i}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{s.h}</h2>
            {s.p?.map((para, j) => (
              <p key={j} className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 mb-3">{para}</p>
            ))}
            {s.ul && (
              <ul className="space-y-2 mt-2">
                {s.ul.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-[15px] text-gray-700 dark:text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-primary-500 mt-1 shrink-0" /><span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* FAQ */}
      {post.faq && (
        <section className="mt-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('toolpage.faq', { defaultValue: 'Časté dotazy' })}</h2>
          <div className="space-y-3">
            {post.faq.map((f, i) => (
              <details key={i} className="group bg-white dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/10 overflow-hidden">
                <summary className="font-semibold text-gray-900 dark:text-white text-sm list-none flex items-center justify-between gap-3 p-4 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-white/5 transition">
                  <span>{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition shrink-0" />
                </summary>
                <p className="text-sm text-gray-600 dark:text-gray-300 px-4 pb-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Související nástroje — interní prolinky (SEO/GEO) */}
      {post.tools && post.tools.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('blog.related_tools', { defaultValue: 'Vyzkoušejte související nástroje' })}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {post.tools.map((tool) => (
              <Link key={tool.to} to={relocalizePath(tool.to, 'cs', lang)}
                className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-white/10 hover:shadow transition">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                  <Wrench className="w-4 h-4 text-primary-500 shrink-0" />{tool.label}
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 shrink-0 transition-colors" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sdílení */}
      <ShareBar url={SITE + canonicalPath} title={post.title} className="mt-10 pt-6 border-t border-gray-100 dark:border-white/10" />

      {/* Související články */}
      {post.related && post.related.length > 0 && (
        <section className="mt-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('blog.related', { defaultValue: 'Související články' })}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {post.related.map((rs) => {
              const r = BLOG_POSTS.find((x) => x.slug === rs)
              if (!r) return null
              // Titulek lokalizuj přes překlad (jinak by se zobrazil česky i v cizí mutaci).
              const rt = (TRANS[i18n.language] || (i18n.language !== 'cs' && TRANS.en) || []).find((x) => x.slug === rs)
              const rTitle = (rt?.title as string) || r.title
              return (
                <Link key={rs} to={blogPath(rs, lang)}
                  className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-white/10 hover:shadow transition">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{rTitle}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 shrink-0 transition-colors" />
                </Link>
              )
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mt-12 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-500 p-8 text-center text-white">
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-90" />
        <h2 className="text-2xl font-extrabold mb-2">{t('toolpage.cta_title', { defaultValue: 'Chcete kompletní SEO + GEO analýzu webu?' })}</h2>
        <Link to="/" className="inline-flex items-center gap-2 bg-white text-primary-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition mt-3">
          {t('toolpage.cta_button', { defaultValue: 'Spustit kompletní analýzu' })}<ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </article>
  )
}
